<template>
  <div class="grid-card">
    <div class="grid-header-bar">
      <h3>Detailed Cashflow Grid</h3>
      <div class="grid-header-right">
        <button class="compact-toggle" @click="compact = !compact" :title="compact ? 'Show full values' : 'Show compact values'">
          {{ compact ? '$1K' : '$1,000' }}
        </button>
        <div class="grid-legend">
          <div class="legend-item"><div class="legend-dot" style="background:#2ba97a;"></div> Paid</div>
          <div class="legend-item"><div class="legend-dot" style="background:#34c38f;"></div> Raised</div>
          <div class="legend-item"><div class="legend-dot" style="background:#3b82f6;"></div> Signed</div>
          <div class="legend-item"><div class="legend-dot" style="background:#e0973a;"></div> Quoted</div>
          <div class="legend-item"><div class="legend-dot" style="background:#8b5cf6;"></div> Recurring</div>
          <div class="legend-item"><div class="legend-dot" style="background:#c5cae0;"></div> Tentative</div>
        </div>
      </div>
    </div>
    <div class="grid-scroll">
      <table>
        <thead>
          <tr>
            <th class="th-name"></th>
            <th
              v-for="(month, idx) in months"
              :key="month"
              :class="{ qs: isQuarterStart(idx) }"
            >
              {{ formatMonth(month) }}
            </th>
            <th class="th-total">FY Total</th>
            <th class="th-action"></th>
          </tr>
        </thead>
        <tbody v-for="(section, status) in groupedRows" :key="status">
          <template v-if="section.length > 0">
            <tr class="section-row" @click="toggleSection(status)">
              <td class="section-name-cell">
                <div class="section-cell">
                  <span class="section-caret" :class="{ collapsed: collapsedSections[status] }">&#9662;</span>
                  <span class="section-name">{{ status }}</span>
                  <span class="section-count">{{ section.length }} {{ sectionLabel(status, section.length) }}</span>
                </div>
              </td>
              <td
                v-for="(month, idx) in months"
                :key="'sec-' + status + '-' + month"
                class="section-month-cell"
                :class="{ qs: isQuarterStart(idx) }"
              >
                {{ formatCents((sectionTotals[status] || {})[month] || 0) }}
              </td>
              <td class="section-total-cell">{{ formatCents((sectionTotals[status] || {})._total || 0) }}</td>
              <td></td>
            </tr>
            <forecast-row
              v-for="row in section"
              v-show="!collapsedSections[status]"
              :key="row.type + '-' + (row.opportunity_id || row.forecast_entry_id || row.sla_id || row.retainer_id || row.budget_id || row.stack_id || row.client_id)"
              :row="row"
              :months="months"
              :compact="compact"
              @update-name="$emit('update-name', $event)"
              @update-month="$emit('update-month', $event)"
              @remove-month="$emit('remove-month', $event)"
              @delete-row="$emit('delete-row', $event)"
            />
          </template>
        </tbody>
        <tfoot>
          <tr class="ft-totals">
            <td class="ft-label">Monthly Totals</td>
            <td
              v-for="(month, idx) in months"
              :key="'total-' + month"
              :class="{ qs: isQuarterStart(idx) }"
            >
              {{ formatCurrencyCompact(totals[month] || 0) }}
            </td>
            <td class="ft-grand">{{ formatCurrencyCompact(grandTotal) }}</td>
            <td></td>
          </tr>
          <tr class="ft-target">
            <td class="ft-label">Target</td>
            <td
              v-for="(month, idx) in months"
              :key="'target-' + month"
              :class="{ qs: isQuarterStart(idx) }"
            >
              {{ formatCurrencyCompact(targets[month] || 0) }}
            </td>
            <td class="ft-grand">{{ formatCurrencyCompact(yearTarget) }}</td>
            <td></td>
          </tr>
          <tr class="ft-remain">
            <td class="ft-label">Remaining</td>
            <td
              v-for="(month, idx) in months"
              :key="'rem-' + month"
              :class="[
                { qs: isQuarterStart(idx) },
                remaining[month] > 0 ? 'short' : 'ok'
              ]"
            >
              {{ remaining[month] > 0 ? '-' : '' }}{{ formatCurrencyCompact(Math.abs(remaining[month] || 0)) }}
            </td>
            <td :class="yearRemaining > 0 ? 'short' : 'ok'" class="ft-grand">
              {{ yearRemaining > 0 ? '-' : '' }}{{ formatCurrencyCompact(Math.abs(yearRemaining)) }}
            </td>
            <td></td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script>
import ForecastRow from './ForecastRow.vue'

export default {
  components: { ForecastRow },

  data() {
    return {
      collapsedSections: {},
      compact: true
    }
  },

  props: {
    groupedRows: { type: Object, required: true },
    months: { type: Array, required: true },
    totals: { type: Object, required: true },
    targets: { type: Object, required: true },
    remaining: { type: Object, required: true },
    // All computed on the backend (ForecastSummaryService)
    sectionTotals: { type: Object, required: true },
    grandTotal: { type: Number, default: 0 },
    yearTarget: { type: Number, default: 0 },
    yearRemaining: { type: Number, default: 0 }
  },

  methods: {
    formatCents(cents) {
      if (this.compact) return this.formatCurrencyCompact(cents)
      const dollars = cents / 100
      return '$' + this.$options.filters.currency(dollars)
    },

    toggleSection(status) {
      this.$set(this.collapsedSections, status, !this.collapsedSections[status])
    },

    formatMonth(month) {
      const [y, m] = month.split('-')
      const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return names[parseInt(m) - 1]
    },

    formatCurrencyCompact(cents) {
      const dollars = Math.abs(cents / 100)
      if (dollars >= 1000000) return '$' + (dollars / 1000000).toFixed(1) + 'M'
      if (dollars >= 1000) return '$' + Math.round(dollars / 1000) + 'K'
      if (dollars === 0) return '$0'
      return '$' + Math.round(dollars)
    },

    isQuarterStart(idx) {
      return idx > 0 && idx % 3 === 0
    },

    sectionColor(status) {
      const colors = {
        'Opportunities': '#8b5cf6',
        'SLA': '#8b5cf6',
        'Retainer': '#8b5cf6',
        'T&M': '#8b5cf6',
        'DevOps': '#8b5cf6',
        'Hosting': '#8b5cf6',
        'Unknown': '#e0973a'
      }
      return colors[status] || '#8b5cf6'
    },

    sectionLabel(status, count) {
      const labels = {
        'Opportunities': ['opportunity', 'opportunities'],
        'SLA': ['SLA', 'SLAs'],
        'Retainer': ['retainer', 'retainers'],
        'T&M': ['budget', 'budgets'],
        'DevOps': ['stack', 'stacks'],
        'Hosting': ['client', 'clients'],
        'Unknown': ['invoice', 'invoices']
      }
      if (labels[status]) {
        return count === 1 ? labels[status][0] : labels[status][1]
      }
      return count === 1 ? 'item' : 'items'
    }
  }
}
</script>

<style scoped>
.grid-card {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e8eaf2;
  overflow: hidden;
}
.grid-header-bar {
  padding: 14px 20px;
  border-bottom: 1px solid #e8eaf2;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.grid-header-bar h3 {
  font-size: 0.85rem;
  font-weight: 600;
  color: #1e1e2d;
  margin: 0;
}
.grid-header-right {
  display: flex;
  align-items: center;
  gap: 14px;
}
.compact-toggle {
  padding: 4px 10px;
  font-size: 0.68rem;
  font-weight: 600;
  color: #8b8fa7;
  background: #f0f1f7;
  border: 1px solid #e2e5f0;
  border-radius: 4px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s;
}
.compact-toggle:hover {
  background: #e2e5f0;
  color: #1e1e2d;
}
.grid-legend {
  display: flex;
  gap: 18px;
}
.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.72rem;
  color: #8b8fa7;
  font-weight: 500;
}
.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 3px;
  flex-shrink: 0;
}

.grid-scroll {
  overflow-x: auto;
}
.grid-scroll::-webkit-scrollbar {
  height: 6px;
}
.grid-scroll::-webkit-scrollbar-thumb {
  background: #d1d5e4;
  border-radius: 3px;
}

table {
  width: max-content;
  min-width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
}

/* Header */
thead th {
  padding: 10px 8px;
  text-align: center;
  font-weight: 600;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #8b8fa7;
  background: #fafbfe;
  border-bottom: 1px solid #e8eaf2;
  white-space: nowrap;
}
.th-name {
  text-align: left;
  padding-left: 20px;
  min-width: 220px;
  position: sticky;
  left: 0;
  z-index: 4;
  background: #fafbfe;
  border-right: 1px solid #e8eaf2;
}
.th-total {
  text-align: right;
  padding-right: 16px;
}
.th-action {
  width: 40px;
}

/* Section rows */
.section-row {
  cursor: pointer;
  user-select: none;
}
.section-row:hover td {
  background: #f3f4fa;
}
.section-cell {
  padding: 8px 20px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.section-caret {
  font-size: 0.7rem;
  color: #8b8fa7;
  transition: transform 0.2s ease;
  display: inline-block;
}
.section-caret.collapsed {
  transform: rotate(-90deg);
}
.section-dot {
  width: 8px;
  height: 8px;
  border-radius: 2px;
  flex-shrink: 0;
}
.section-name {
  font-weight: 700;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #1e1e2d;
}
.section-count {
  font-size: 0.68rem;
  color: #8b8fa7;
  font-weight: 500;
}
.section-name-cell {
  padding: 0;
  background: #fafbfe;
  border-bottom: 1px solid #f0f1f7;
  position: sticky;
  left: 0;
  z-index: 3;
  border-right: 1px solid #e8eaf2;
  min-width: 220px;
}
.section-row:hover .section-name-cell {
  background: #f3f4fa;
}
.section-month-cell {
  padding: 8px 8px;
  background: #fafbfe;
  border-bottom: 1px solid #f0f1f7;
  text-align: center;
  font-size: 0.7rem;
  font-weight: 500;
  color: #b0b4c8;
  white-space: nowrap;
}
.section-row:hover .section-month-cell {
  background: #f3f4fa;
}
.section-total-cell {
  padding: 8px 16px;
  background: #fafbfe;
  border-bottom: 1px solid #f0f1f7;
  text-align: right;
  font-size: 0.7rem;
  font-weight: 600;
  color: #b0b4c8;
  white-space: nowrap;
}

/* Quarter separator */
.qs {
  border-left: 2px solid #e2e5f0 !important;
}

/* Footer */
tfoot td {
  padding: 10px 8px;
  text-align: center;
  font-weight: 600;
  font-size: 0.78rem;
  white-space: nowrap;
  border-top: 2px solid #e8eaf2;
}
.ft-label {
  text-align: left;
  padding-left: 20px;
  position: sticky;
  left: 0;
  z-index: 2;
  background: #fff;
  border-right: 1px solid #e8eaf2;
}
.ft-grand {
  text-align: right;
  padding-right: 16px;
}
.ft-totals td {
  background: #fafbfe;
  color: #1e1e2d;
}
.ft-totals .ft-label {
  background: #fafbfe;
}
.ft-target td {
  background: #faf8f0;
  color: #b8860b;
  font-weight: 500;
}
.ft-target .ft-label {
  background: #faf8f0;
}
.ft-remain td {
  background: #fff;
}
.ft-remain .ft-label {
  background: #fff;
}
.ft-remain .short {
  color: #ff6b6b;
  font-weight: 600;
}
.ft-remain .ok {
  color: #34c38f;
  font-weight: 600;
}
</style>
